import React, { ReactElement, useEffect, useMemo } from 'react';

import { getClassNames } from '../../../../utils/util';
import { specialCharacters } from '../../../../utils/constants';

import useTheme from '../../../../hooks/use-theme/useTheme';

import '../validation.scss';

const WEAK: string = 'weak';
const MEDIUM: string = 'medium';
const STRONG: string = 'strong';

type PasswordStrength = typeof WEAK | typeof MEDIUM | typeof STRONG;

type Props = {
  password: string;
  confirmationPassword: string;
  setValidPassword: (bool: boolean) => void;
};

const PasswordValidation = ({ password, confirmationPassword, setValidPassword }: Props): ReactElement => {
  // TODO: Update usage after migration
  const { pps } = useTheme();

  const hasSpecialCharacterOrNumber: boolean = useMemo((): boolean => {
    if (!password) return false;

    const hasSpecialCharacter: boolean = specialCharacters.some((character: string): boolean =>
      password.includes(character)
    );

    if (!hasSpecialCharacter) {
      return password.split('').some((character: string): boolean => !isNaN(Number(character)));
    }

    return hasSpecialCharacter;
  }, [password]);

  const passwordStrength: PasswordStrength = useMemo((): PasswordStrength => {
    const hasEightCharacters: boolean = password.length > 7;

    if (hasEightCharacters && hasSpecialCharacterOrNumber) {
      return STRONG;
    } else if (hasEightCharacters) {
      return MEDIUM;
    }

    return WEAK;
  }, [password, hasSpecialCharacterOrNumber]);

  useEffect((): void => {
    setValidPassword(passwordStrength === STRONG && password === confirmationPassword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmationPassword, password, passwordStrength]);

  return (
    <div className={getClassNames('validation', { pps })}>
      <div className="validation__rule">
        <span className={getClassNames('validation__rule__bullet', { satisfied: password.length > 7, pps })} />
        <p className={getClassNames('validation__rule__text', { pps })}>At least 8 characters</p>
      </div>
      <div
        className={getClassNames('validation__indicator', {
          strong: passwordStrength === STRONG,
          weak: passwordStrength === WEAK,
          pps
        })}
      >
        {passwordStrength}
      </div>

      <div className="validation__rule">
        <span className={getClassNames('validation__rule__bullet', { satisfied: hasSpecialCharacterOrNumber, pps })} />
        <p className={getClassNames('validation__rule__text', { pps })}>Number or special character</p>
      </div>

      {confirmationPassword !== undefined && (
        <>
          <span />
          <div className="validation__rule">
            <span
              className={getClassNames('validation__rule__bullet', {
                satisfied: password && password === confirmationPassword,
                pps
              })}
            />
            <p className={getClassNames('validation__rule__text', { pps })}>Passwords match</p>
          </div>
        </>
      )}
    </div>
  );
};

export default PasswordValidation;
